import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { ArrowLeft, Clock, Loader2, MailOpen, Sparkles } from "lucide-react";

import { GENERATE_PROGRESS_REPORT } from "@/graphql/mutations/reports";
import { GET_PROGRESS_REPORTS } from "@/graphql/queries/reports";
import { ProgressLetterCard, ProgressLetterMeter } from "@/components/progress-letter";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/AuthContext";

/**
 * „Kaip tau sekasi" — the learner's own progress, and never the word „ataskaita".
 *
 * What arrives is a **letter**: their name at the top, what they managed, one thing
 * to try. A report is what a school sends home *about* a child; a letter is written
 * *to* them, and that is what this text actually is.
 *
 * A letter is deliberately not always available — once a week, and only after three
 * sessions of one subject. So the page's usual state is not a letter but one on its
 * way, which is the only framing under which "not yet" is something to look forward
 * to. A disabled button with a rule beside it would just be a locked door.
 *
 * Nothing here can reach the grown-up's report: the server only ever serves this
 * role its own, whatever is asked for.
 */
export default function KidProgressReportsPage() {
  const { student: me } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GET_PROGRESS_REPORTS, {
    variables: { studentId: me?.id },
    skip: !me?.id,
    fetchPolicy: "cache-and-network",
  });

  const [generate, { loading: generating }] = useMutation(GENERATE_PROGRESS_REPORT);

  // Newest on top. The server already orders it this way; stating it here means the
  // page cannot start listing letters backwards because a query changed.
  const letters = [...((data as any)?.progressReports ?? [])].sort(
    (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const availability = (data as any)?.progressReportAvailability;
  const latest = letters[0];
  const older = letters.slice(1);

  async function onGenerate() {
    setError(null);
    try {
      const result = await generate({ variables: { studentId: me?.id } });
      const payload = (result.data as any)?.generateProgressReport;
      if (payload?.errors?.length) setError(payload.errors[0]);
      await refetch();
    } catch {
      setError("Nepavyko atidaryti laiško. Pabandyk vėliau.");
    }
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const waitingLetter = availability?.allowed === true;

  // The letter that has arrived: the loudest thing on the page, so it leads.
  const arrival = availability && waitingLetter ? (
    <div className="space-y-3">
      <ProgressLetterMeter availability={availability} />

      <Button onClick={onGenerate} disabled={generating} size="lg" className="w-full rounded-2xl">
        {generating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Atidaroma...
          </>
        ) : (
          <>
            <MailOpen className="mr-2 h-4 w-4" />
            {letters.length > 0 ? "Atidaryti naują laišką" : "Atidaryti pirmą laišką"}
          </>
        )}
      </Button>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  ) : null;

  // What the next letter is waiting for. This sits at the very bottom, after
  // every letter there is to read: "when does the next one come?" is the question
  // a child has once they have finished reading, not before they start. Putting
  // it first made the page open on a requirement instead of on their own news.
  const nextLetter = availability && !waitingLetter ? (
    <div className="space-y-3">
      <h2 className="flex items-center gap-2 px-1 text-sm font-semibold text-muted-foreground">
        <Clock className="h-4 w-4" />
        Kada gausi naują laišką
      </h2>

      <ProgressLetterMeter availability={availability} />

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      {/* Hero — the same warmth the profile page opens with, so this reads as part
          of the child's own world rather than as a records screen. */}
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-amber-500/10 dark:from-indigo-500/20 dark:via-purple-500/20 dark:to-amber-500/20">
        <div className="pointer-events-none absolute -top-24 -right-20 h-64 w-64 rounded-full bg-gradient-to-br from-purple-400/30 to-pink-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-gradient-to-br from-amber-400/30 to-orange-400/30 blur-3xl" />

        <div className="relative p-4 sm:p-6">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full bg-background/40 backdrop-blur-sm hover:bg-background/70"
            asChild
          >
            <Link to="/profile" aria-label="Atgal">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="relative flex flex-col items-center gap-2 px-6 pb-9 text-center">
          <span className="text-5xl">💌</span>
          <h1 className="bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-3xl font-extrabold tracking-tight text-transparent sm:text-4xl">
            Kaip tau sekasi
          </h1>
          <p className="max-w-md text-sm text-muted-foreground sm:text-base">
            Laiškai tau apie tai, ką jau moki ir kuo gali didžiuotis
          </p>
        </div>
      </div>

      {/* Arrival first, then the newest letter, then when the next one comes, and
          the archive under that: the question "when do I get another?" belongs
          after the letter they have, and before the ones they have already read. */}
      {arrival}

      {latest ? (
        <ProgressLetterCard report={latest} fresh={!waitingLetter} defaultOpen />
      ) : (
        !waitingLetter && (
          <div className="rounded-3xl border border-dashed py-12 text-center">
            <p className="text-4xl">✉️</p>
            <p className="mt-3 font-semibold">Pirmasis laiškas dar kelyje</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
              Išspręsk tris tos pačios temos sesijas — ir tau atkeliaus laiškas apie tai, kas
              jau pavyko.
            </p>
          </div>
        )
      )}

      {nextLetter}

      {/* Every letter is kept and every one reopens — closed by default, because a
          letter a child came back for should be findable by its title rather than
          scrolled past. */}
      {older.length > 0 && (
        <div className="space-y-3">
          <h2 className="flex items-center gap-2 px-1 text-sm font-semibold text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            Ankstesni laiškai
            <span className="font-normal">({older.length})</span>
          </h2>

          {older.map((letter: any) => (
            <ProgressLetterCard key={letter.id} report={letter} />
          ))}
        </div>
      )}
    </div>
  );
}
